import type { Book } from "../../shared/models";
import { legacyBookToDesktopV2 } from "../../shared/legacyBookToDesktopV2";
import {
  editorBookFilePath,
  readEditorWorkspaceRoot,
} from "../../shared/editorWorkspace";
import { isAccountPrimaryRuntime, isWebRuntime } from "../lib/runtime";
import { cloudAuthDesktopV2 } from "./cloudAuthDesktopV2";
import { fetchDesktopLocalCopyPath, registerDesktopLocalCopy } from "./desktopLocalCopy";
import { getCachedCloudBookLocalPath, rememberCloudBookLocalPath } from "./cloudBookPaths";
import { resolveAutoSyncMode } from "./desktopV2AutoSync";
import {
  clientNameForBook,
  listEditorEngagements,
  versionFolderNameForGrant,
} from "./editorEngagements";
import { cloudVirtualPath, writeWebLastSession } from "./webIdbStore";

export type CloudBookSummary = {
  id: string;
  title: string;
  author: string;
  updatedAt: string;
  chapterCount: number;
};

export function isCloudOpenAvailable(): boolean {
  return resolveAutoSyncMode() === "web";
}

export type CloudBookLocalConnection = {
  cloudBookId: string;
  localFilePath: string;
};

async function pathStillExists(filePath: string): Promise<boolean> {
  try {
    return await window.desktopV2Api.pathExists(filePath);
  } catch {
    return false;
  }
}

/**
 * Resolve a local file path already connected to this cloud book.
 * Order: cloud DB (this computer) → local map → current session.
 */
export async function resolveCloudBookLocalConnection(
  cloudBookId: string,
  session?: { localBookId?: string | null; localFilePath?: string | null }
): Promise<CloudBookLocalConnection | null> {
  const id = cloudBookId.trim();
  if (!id) return null;

  const fromCloud = await fetchDesktopLocalCopyPath(id);
  if (fromCloud) {
    return { cloudBookId: id, localFilePath: fromCloud };
  }

  const storedPath = getCachedCloudBookLocalPath(id);
  if (storedPath && (await pathStillExists(storedPath))) {
    return { cloudBookId: id, localFilePath: storedPath };
  }

  const sessionBookId = typeof session?.localBookId === "string" ? session.localBookId.trim() : "";
  const sessionPath = typeof session?.localFilePath === "string" ? session.localFilePath.trim() : "";
  if (sessionBookId === id && sessionPath && (await pathStillExists(sessionPath))) {
    return { cloudBookId: id, localFilePath: sessionPath };
  }

  return null;
}

async function loadRemotePayload(bookId: string): Promise<unknown> {
  if (resolveAutoSyncMode() !== "web") {
    throw new Error(
      isAccountPrimaryRuntime()
        ? "Sign in to open books from your account."
        : "Sign in under Settings → Account to open books from your library."
    );
  }
  return cloudAuthDesktopV2.loadBook(bookId);
}

export async function listOwnedCloudBooks(): Promise<CloudBookSummary[]> {
  if (resolveAutoSyncMode() !== "web") return [];
  return cloudAuthDesktopV2.listOwnedBooks();
}

/** Fetch full book JSON from cloud and convert to desktop-v2 shape (does not write disk). */
export async function fetchCloudBookDesktop(bookId: string): Promise<Book> {
  const payload = await loadRemotePayload(bookId);
  return legacyBookToDesktopV2(payload);
}

export type OpenCloudBookResult =
  | { ok: true; book: Book; filePath: string; persistedBookId?: string; linkedExisting?: boolean }
  | { ok: false; canceled: true }
  | { ok: false; error: string };

export type OpenCloudBookToDiskOptions = {
  /** When the workbench already has this cloud book open on disk, skip Save As. */
  session?: { localBookId?: string | null; localFilePath?: string | null };
};

/**
 * Open a cloud book in memory (web SPA) — no local .sbk.
 */
export async function openCloudBookInMemory(book: Book): Promise<OpenCloudBookResult> {
  const bookId = typeof book.id === "string" ? book.id.trim() : "";
  if (!bookId) {
    return { ok: false, error: "Cloud book has no id" };
  }
  const path = cloudVirtualPath(bookId);
  try {
    await window.desktopV2Api.saveBookAtPath({ book, filePath: path });
  } catch {
    // Draft cache / sync may fail offline; still open in memory.
  }
  await writeWebLastSession({
    filePath: path,
    bookId,
    updatedAt: new Date().toISOString(),
  });
  return { ok: true, book, filePath: path, persistedBookId: bookId, linkedExisting: false };
}

/**
 * Load book from library, save as `.sbk` on Electron (or virtual cloud path on web).
 */
export async function openCloudBookToDisk(
  book: Book,
  options?: OpenCloudBookToDiskOptions
): Promise<OpenCloudBookResult> {
  if (isWebRuntime()) {
    return openCloudBookInMemory(book);
  }

  const bookId = typeof book.id === "string" ? book.id.trim() : "";
  const connection = bookId ? await resolveCloudBookLocalConnection(bookId, options?.session) : null;
  const existingPath = connection?.localFilePath;

  if (existingPath) {
    try {
      const saved = await window.desktopV2Api.saveBookAtPath({ book, filePath: existingPath });
      if (bookId) {
        rememberCloudBookLocalPath(bookId, saved.filePath);
        await registerDesktopLocalCopy(bookId, saved.filePath);
      }
      return {
        ok: true,
        book,
        filePath: saved.filePath,
        persistedBookId: saved.persistedBookId,
        linkedExisting: true,
      };
    } catch {
      // fall through to Save As
    }
  }

  // Editor workspace: prefer Client/Book/Version path when configured
  const workspaceRoot = readEditorWorkspaceRoot();
  let defaultPathHint: string | undefined;
  if (workspaceRoot && bookId) {
    try {
      const engagements = await listEditorEngagements();
      const clientName = clientNameForBook(engagements, bookId);
      if (clientName) {
        const versionName = versionFolderNameForGrant(
          engagements,
          bookId,
          book.metadata as Record<string, unknown>
        );
        defaultPathHint = editorBookFilePath({
          workspaceRoot,
          clientName,
          bookTitle: book.title || "Untitled",
          versionName,
        });
        // If the suggested path already exists, write there without a dialog
        if (await pathStillExists(defaultPathHint)) {
          const saved = await window.desktopV2Api.saveBookAtPath({
            book,
            filePath: defaultPathHint,
          });
          rememberCloudBookLocalPath(bookId, saved.filePath);
          await registerDesktopLocalCopy(bookId, saved.filePath);
          return {
            ok: true,
            book,
            filePath: saved.filePath,
            persistedBookId: saved.persistedBookId,
            linkedExisting: true,
          };
        }
      }
    } catch {
      // fall through
    }
  }

  const result = await window.desktopV2Api.saveBook(book, true, defaultPathHint);
  if (result.canceled || !result.filePath) {
    return { ok: false, canceled: true };
  }
  if (bookId) {
    rememberCloudBookLocalPath(bookId, result.filePath);
    await registerDesktopLocalCopy(bookId, result.filePath);
  }
  return {
    ok: true,
    book,
    filePath: result.filePath,
    persistedBookId: result.persistedBookId,
    linkedExisting: false,
  };
}
